import { showError } from 'helpers/handleError';
import { useLayoutEffect, useState, useCallback, useRef } from 'react';
import UserServices from 'services/UserServices';

//* GUIDE:
//**************************************
//* Check services of API              *
//* Check parseBodyToRequest function  *
//* Check checkConditionPass function  *
//**************************************

/**
 * @returns {{
 *  originalInfo: object,
 *  draftInfo: object,
 *  noNeedUpdate: boolean,
 *  isVerified: boolean,
 *  isNotHaveBilling: boolean,
 *  isInProgress: boolean,
 *  isRejected: boolean,
 *  rejectReason: string,
 *  data: { draftInfo: {}, isVerified: false, isNotHaveBilling: false, isInProgress: false, originalInfo: {} },
 *  loading: boolean,
 *  refetching: boolean,
 *  error: string | any,
 *  refetch: () => Promise<void>,
 *  refetchWithLoading: () => Promise<void>,
 *  setData: Dispatch<SetStateAction<any[]>>,
 * }}
 */

const useCheckingBillingInformation = ({ isTrigger = true } = {}) => {
  const controller = useRef(new AbortController());
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetching, setRefetching] = useState(false);

  //! Function
  const parseResponse = useCallback((response) => {
    // case 1: có last request (200 và body request)
    // case 2: thiếu billingInfo và ko có request (400 và msg)
    // case 3: đủ billingInfo và ko có request (200 và empty body) (edited)

    // case 1 ra 200,
    // case 2 ra 202
    // case 3 ra 204
    const noNeedUpdate =
      response?.status === 204 ||
      response?.status === 304 ||
      (response?.status === 200 && !response?.data);
    const status = response?.data?.status;

    return {
      noNeedUpdate: noNeedUpdate,
      isVerified: !noNeedUpdate && response?.data?.status === 'RELEASED',
      isInProgress: !noNeedUpdate && (status === 'APPROVED' || status === 'PENDING'),
      isRejected: !noNeedUpdate && status === 'REJECTED',
      isNotHaveBilling:
        !noNeedUpdate && response?.data === 'Please update billing information first',
      draftInfo: response?.data?.billingInfo,
      originalInfo: response?.data?.metaData?.oldData,
      rejectReason: response?.data?.metaData?.rejectReason,
    };
  }, []);

  const checkConditionPass = useCallback((response) => {
    setData(parseResponse(response));
  }, []);

  const fetch = useCallback(() => {
    if (!isTrigger) {
      return;
    }

    console.log('[TRIGGER]: useCheckingBillingInformation()');
    return new Promise(async (resolve, reject) => {
      try {
        const response = await UserServices.checkBillingInformation({
          signal: controller.current.signal,
        });

        resolve(response);
      } catch (error) {
        setError(error);
        reject(error);
      }
    });
  }, [isTrigger]);

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(
    async (onSuccess) => {
      try {
        setRefetching(true);
        const response = await fetch();
        checkConditionPass(response);
        onSuccess && onSuccess(parseResponse(response));
      } catch (error) {
        showError(error);
      } finally {
        setRefetching(false);
      }
    },
    [fetch, checkConditionPass, parseResponse]
  );

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(
    async (onSuccess) => {
      try {
        setLoading(true);
        const response = await fetch();
        checkConditionPass(response);
        onSuccess && onSuccess(parseResponse(response));
        setLoading(false);
      } catch (error) {
        showError(error);
      } finally {
        setLoading(false);
      }
    },
    [fetch, checkConditionPass, parseResponse]
  );

  useLayoutEffect(() => {
    let shouldSetData = true;
    controller.current = new AbortController();

    const fetchAPI = async () => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
          setLoading(false);
        }
      } catch (error) {
        // showError(error);
        checkConditionPass(error);
        setLoading(false);
      }
    };

    fetchAPI();

    return () => {
      controller.current.abort();
      shouldSetData = false;
    };
  }, [fetch]);

  return {
    originalInfo: data?.originalInfo,
    draftInfo: data?.draftInfo,
    isVerified: data?.isVerified,
    isInProgress: data?.isInProgress,
    isNotHaveBilling: data?.isNotHaveBilling,
    isRejected: data?.isRejected,
    rejectReason: data?.rejectReason,
    noNeedUpdate: data?.noNeedUpdate,

    data,
    loading,
    error,
    refetching,
    refetch,
    refetchWithLoading,
    setData,
  };
};

export default useCheckingBillingInformation;
